import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form sadece görsel - backend yok
    toast.success('Mesajınız alındı! En kısa sürede size geri döneceğiz.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Telefon',
      content: '0530 512 80 90',
      link: 'tel:+905305128090',
      color: 'from-sage-green to-mint-green'
    },
    {
      icon: Mail,
      title: 'E-posta',
      content: 'info@kavipsikoloji.com',
      link: 'mailto:info@kavipsikoloji.com',
      color: 'from-forest-green to-sage-green'
    },
    {
      icon: MapPin,
      title: 'Adres',
      content: 'Birlik Mahallesi 488. Sokak 8/A Çankaya/ANKARA',
      link: '#map',
      color: 'from-mint-green to-light-sage'
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      content: 'Pzt-Cmt: 09:00-19:00',
      link: null,
      color: 'from-sage-green to-forest-green'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-beige-primary via-beige-secondary to-beige-primary overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sage-green/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-mint-green/5 rounded-full blur-3xl"></div>
        
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-sage-green/10 rounded-full mb-6">
              <span className="text-sage-green font-medium text-sm">İletişim</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-forest-green mb-6">
              Bize Ulaşın
            </h1>
            <p className="text-xl text-text-light leading-relaxed">
              Sorularınızı yanıtlamak ve size yardımcı olmak için buradayız. Bizimle iletişime geçmekten çekinmeyin.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="p-6 border-none shadow-lg card-hover bg-white text-center">
                  <div className={`w-14 h-14 mx-auto bg-gradient-to-br ${info.color} rounded-2xl flex items-center justify-center mb-4 transform hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-forest-green mb-2">{info.title}</h3>
                  {info.link ? (
                    <a href={info.link} className="text-text-light hover:text-sage-green transition-colors text-sm">
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-text-light text-sm">{info.content}</p>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="section-padding bg-gradient-to-br from-beige-primary to-white">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-forest-green mb-4">
                  Mesaj Gönderin
                </h2>
                <p className="text-lg text-text-light mb-8 leading-relaxed">
                  Formu doldurarak bize ulaşabilir, sorularınızı sorabilir veya randevu talebinde bulunabilirsiniz.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-forest-green font-medium">Ad Soyad *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Adınız ve soyadınız"
                      required
                      className="border-sage-green/30 focus:border-sage-green focus:ring-sage-green"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-forest-green font-medium">Telefon *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="0555 123 45 67"
                      required
                      className="border-sage-green/30 focus:border-sage-green focus:ring-sage-green"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-forest-green font-medium">E-posta *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="ornek@email.com"
                    required
                    className="border-sage-green/30 focus:border-sage-green focus:ring-sage-green"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-forest-green font-medium">Konu *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Mesaj konusu"
                    required
                    className="border-sage-green/30 focus:border-sage-green focus:ring-sage-green"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-forest-green font-medium">Mesaj *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Mesajınızı buraya yazın..."
                    required
                    rows={6}
                    className="border-sage-green/30 focus:border-sage-green focus:ring-sage-green resize-none"
                  />
                </div>

                <Button type="submit" size="lg" className="w-full bg-sage-green hover:bg-forest-green text-white button-hover">
                  <Send className="w-5 h-5 mr-2" />
                  Mesaj Gönder
                </Button>
              </form>
            </div>

            {/* Map */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-forest-green mb-4">
                  Ofisimizi Ziyaret Edin
                </h2>
                <p className="text-lg text-text-light">
                  Çankaya Birlik Mahallesi'ndeki modern ve huzurlu ofisimizde sizi ağırlamaktan mutluluk duyarız.
                </p>
              </div>

              <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[500px] group">
                <iframe
                  src="https://www.google.com/maps?q=Birlik+Mahallesi+488.+Sokak+8/A+Çankaya+Ankara&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Kavi Psikoloji Ofis Konumu"
                  className="grayscale hover:grayscale-0 transition-all duration-500"
                ></iframe>
              </div>

              <Card className="mt-6 p-6 border-none shadow-lg bg-white">
                <h3 className="text-xl font-semibold text-forest-green mb-4">Ulaşım Bilgileri</h3>
                <ul className="space-y-2 text-text-light">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-sage-green rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Metro: Ankaray ve Metro hatlarına yakın konumda</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-sage-green rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Otobüs: Çankaya merkezine ulaşan tüm hatlar</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-sage-green rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Otopark: Bina önlerinde ücretsiz otopark</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ or Additional Info */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-forest-green mb-6">
              Randevu Almak İçin Hazır mısınız?
            </h2>
            <p className="text-lg text-text-light mb-8">
              Psikolojik sağlığınız için ilk adımı atmanın tam zamanı. Hemen iletişime geçin ve size uygun randevu saatini belirleyin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+905305128090">
                <Button size="lg" className="bg-sage-green hover:bg-forest-green text-white button-hover">
                  <Phone className="mr-2 w-5 h-5" />
                  Hemen Ara: 0530 512 80 90
                </Button>
              </a>
              <a href="mailto:info@kavipsikoloji.com">
                <Button size="lg" variant="outline" className="border-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white button-hover">
                  <Mail className="mr-2 w-5 h-5" />
                  E-posta Gönder
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};